// FRONT-3338: the composer's thinking control — an always-visible pill that reads
// "Thinking: <level>" in the level's colour. Clicking it opens a small panel with
// the thinking slider (a label under every stop) and one line saying what the
// chosen level does. Replaces the inline slider that used to sit beside the model
// button: the level is now readable at a glance, and the slider no longer crowds
// the composer row.

import { useId, useState, type ComponentProps } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { ThinkingSlider } from '~/components/thinking-slider';
import { AUTO_RAMP, thinkingPillSurface, thinkingPillView, type ThinkingPillView } from '~/lib/thinking-stops';
import type { ThinkingLevel } from '~/lib/types/llm';

// The one pill shape of the composer row: the model button beside the thinking pill imports it too (size "xs" fixes the
// height), so the two can never drift apart and the row never grows.
export const COMPOSER_PILL = 'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors active:scale-95 disabled:opacity-50';

// The word "Thinking:" is read against the level's tint, where the plain secondary ink measured 4.06:1 (it needs
// 4.5:1). One ink step toward the text colour, from tokens, so both looks follow the theme.
const LABEL_INK = 'color-mix(in srgb, var(--muted-foreground) 50%, var(--foreground))';

interface ThinkingPillProps {
  /** The stored level: null is a loaded Auto; undefined is a setting that has not answered (or failed): "Thinking: …". */
  level: ThinkingLevel | null | undefined;
  onChange: (level: ThinkingLevel | null) => void;
  /** Why the control cannot be used right now (`thinkingControlReason`); null when it can. */
  disabledReason: string | null;
  /** The model in use has no Very high / Pro of its own and thinks at High there. */
  cappedAtHigh: boolean;
}

/**
 * Disabled iff there is a reason: the branch below is the only place that decides, so the dimmed pill and its
 * explanation cannot drift apart. Deliberately free of state: the panel's open flag lives in the enabled branch
 * only, so an answer that starts (a reason appears) unmounts it and the panel with it, and it can never pop back
 * open when the answer ends — the pill comes back closed.
 */
export function ComposerThinkingPill({ level, onChange, disabledReason, cappedAtHigh }: ThinkingPillProps) {
  if (disabledReason !== null) {
    return <DisabledThinkingPill level={level} disabledReason={disabledReason} cappedAtHigh={cappedAtHigh} />;
  }
  return <EnabledThinkingPill level={level} onChange={onChange} cappedAtHigh={cappedAtHigh} />;
}

/** The pill itself, one definition for both branches. Props from a wrapping trigger (ref, click, aria) land on the button. */
function ThinkingPillButton({ view, ...rest }: { view: ThinkingPillView } & ComponentProps<typeof Button>) {
  // A level colour only once the setting has loaded: before that, a plain border and the pill's own ink (see thinkingPillSurface).
  const tint = thinkingPillSurface(view);
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      data-testid="composer-thinking-pill"
      data-level={view.dataLevel}
      data-capped={view.capped || undefined}
      // Before the setting has loaded, a screen reader hears what the pill is for, not "Thinking: …" (same as the phone).
      aria-label={view.loaded ? undefined : 'Thinking level'}
      className={cn(COMPOSER_PILL, 'text-muted-foreground hover:text-foreground')}
      style={{ background: tint.background, borderColor: tint.borderColor }}
      {...rest}
    >
      <span
        aria-hidden="true"
        data-testid="composer-thinking-pill-dot"
        className="size-2 shrink-0 rounded-full"
        style={{ background: view.auto ? AUTO_RAMP : view.color }}
      />
      <span className="whitespace-nowrap">
        <span data-testid="composer-thinking-pill-label" style={{ color: LABEL_INK }}>
          Thinking:
        </span>{' '}
        <span
          data-testid="composer-thinking-pill-name"
          className={cn('font-semibold', view.capped && 'underline decoration-dotted underline-offset-2')}
          style={{ color: tint.nameColor }}
        >
          {view.levelName}
        </span>
      </span>
      <CaretDownIcon size={11} weight="bold" className="shrink-0" />
    </Button>
  );
}

/** A reason to wait: the pill stays on screen, dimmed, cannot open, and says why (a hint on hover, and to a screen reader). */
function DisabledThinkingPill({
  level,
  disabledReason,
  cappedAtHigh,
}: Pick<ThinkingPillProps, 'level' | 'cappedAtHigh'> & { disabledReason: string }) {
  const reasonId = useId();
  return (
    <>
      <ThinkingPillButton
        view={thinkingPillView(level, cappedAtHigh)}
        tooltip={disabledReason}
        disabled
        aria-describedby={reasonId}
      />
      <span id={reasonId} className="sr-only" data-testid="composer-thinking-pill-reason">
        {disabledReason}
      </span>
    </>
  );
}

/** Usable: owns the panel's open flag (see `ComposerThinkingPill` for why it lives here and nowhere else). */
function EnabledThinkingPill({ level, onChange, cappedAtHigh }: Omit<ThinkingPillProps, 'disabledReason'>) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ThinkingPillButton view={thinkingPillView(level, cappedAtHigh)} />
      </PopoverTrigger>
      {/* Opens upward: the composer sits at the bottom of the window. Escape and an outside click close only this panel. Wide enough (w-96) that the longest line, the capped Very high sentence, fits the two lines the panel reserves: at w-80 it took three. */}
      <PopoverContent side="top" align="start" aria-label="Thinking" data-testid="composer-thinking-popover" className="w-96 gap-2 p-3">
        <ComposerThinkingPanel level={level} onChange={onChange} cappedAtHigh={cappedAtHigh} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * What the panel holds: a heading, the slider with a label under every stop, and one line about the chosen
 * level (or why it is capped). Its own component so a test can render it: Radix renders no popover content
 * on the server, so the panel is unreachable through the pill's markup string.
 */
export function ComposerThinkingPanel({
  level,
  onChange,
  cappedAtHigh,
}: Omit<ThinkingPillProps, 'disabledReason'>) {
  const view = thinkingPillView(level, cappedAtHigh);
  return (
    <>
      <div className="text-xs font-medium">Thinking</div>
      {/* The panel prints the level's line itself, so the slider's own hover hint would only repeat it, and one opened by the panel's initial focus swallows the first Escape. */}
      <ThinkingSlider
        value={level}
        onChange={onChange}
        cappedAtHigh={cappedAtHigh}
        tooltip={false}
        ariaLabel="Thinking level"
        testId="composer-thinking-slider"
      />
      {/* Two lines reserved, counted in lines (2lh), not rem: the density setting scales the root font size and a fixed height then reserves only one and a bit. The longer lines (Very high, Pro, the capped sentence) wrap, and the panel opens upward, so a line that grew would lift the slider away from the pointer. */}
      <p data-testid="composer-thinking-hint" className="min-h-[2lh] text-xs text-muted-foreground">
        {view.hint}
      </p>
    </>
  );
}
